// RegisterScreen.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from './assets/logo.png';

const API_URL = `${process.env.REACT_APP_API_URL}/api/auth`;

function RegisterScreen() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');

  // Validation
  if (!formData.username || !formData.password || !formData.confirmPassword) {
    setError('All fields are required');
    return;
  }

  if (formData.password.length < 6) {
    setError('Password must be at least 6 characters long');
    return;
  }

  if (formData.password !== formData.confirmPassword) {
    setError('Passwords do not match');
    return;
  }

  setLoading(true);

  try {
    const response = await fetch(`${API_URL}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: formData.username,
        password: formData.password
      })
    });

    const data = await response.json();

    if (data.success) {
      // Store user info in localStorage (NO TOKEN)
      localStorage.setItem('userId', data.data.userId);
      localStorage.setItem('username', data.data.username);
      localStorage.setItem('isLoggedIn', 'true');
      
      // Redirect to admin dashboard
      navigate('/admin-dashboard');
    } else {
      setError(data.message || 'Registration failed');
    }
  } catch (err) {
    console.error('Registration error:', err);
    setError('Network error. Please try again.');
  } finally {
    setLoading(false);
  }
};

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F1419 0%, #1A1F2E 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(1rem, 3vw, 2rem)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative Background */}
      <div style={{
        position: 'absolute',
        top: '-10%',
        right: '-10%',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(199, 179, 129, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none'
      }}></div>
      <div style={{
        position: 'absolute',
        bottom: '-10%',
        left: '-10%',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(199, 179, 129, 0.05) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none'
      }}></div>

      {/* Registration Card */}
      <div style={{
        width: '100%',
        maxWidth: '480px',
        background: 'rgba(15, 20, 25, 0.95)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(199, 179, 129, 0.2)',
        borderRadius: '16px',
        padding: isMobile ? 'clamp(2rem, 5vw, 3rem)' : 'clamp(2.5rem, 5vw, 3.5rem)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Logo & Title */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
          <Link to="/" style={{ display: 'inline-block', marginBottom: '1.5rem' }}>
            <img 
              src={logo} 
              alt="ST Motors Logo" 
              style={{
                height: 'clamp(60px, 10vw, 80px)',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </Link>
          <h1 style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            fontWeight: '300',
            color: '#FFFFFF',
            marginBottom: '0.5rem',
            letterSpacing: '-0.01em'
          }}>
            Create <span style={{ fontWeight: '600', color: '#C7B381' }}>Account</span>
          </h1>
          <p style={{
            fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
            color: '#B8B8B8',
            fontWeight: '300'
          }}>
            Register to access admin dashboard
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: 'rgba(220, 53, 69, 0.1)',
            border: '1px solid rgba(220, 53, 69, 0.3)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#ff6b6b',
            fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* Registration Form */}
        <form onSubmit={handleSubmit}>
          {/* Username Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)',
              color: '#C7B381',
              marginBottom: '0.5rem',
              fontWeight: '500',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>
              Username
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter username"
              style={{
                width: '100%',
                padding: 'clamp(0.9rem, 2vw, 1.1rem)',
                background: 'rgba(26, 31, 46, 0.6)',
                border: '1px solid rgba(199, 179, 129, 0.2)',
                borderRadius: '8px',
                fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
                color: '#FFFFFF',
                outline: 'none',
                transition: 'all 0.3s ease',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#C7B381';
                e.target.style.background = 'rgba(26, 31, 46, 0.8)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(199, 179, 129, 0.2)';
                e.target.style.background = 'rgba(26, 31, 46, 0.6)';
              }}
            />
          </div>

          {/* Password Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)',
              color: '#C7B381',
              marginBottom: '0.5rem',
              fontWeight: '500',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password"
                style={{
                  width: '100%',
                  padding: 'clamp(0.9rem, 2vw, 1.1rem)',
                  paddingRight: '3rem',
                  background: 'rgba(26, 31, 46, 0.6)',
                  border: '1px solid rgba(199, 179, 129, 0.2)',
                  borderRadius: '8px',
                  fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
                  color: '#FFFFFF',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#C7B381';
                  e.target.style.background = 'rgba(26, 31, 46, 0.8)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(199, 179, 129, 0.2)';
                  e.target.style.background = 'rgba(26, 31, 46, 0.6)';
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#C7B381',
                  cursor: 'pointer',
                  fontSize: '1.2rem'
                }}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Confirm Password Field */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)',
              color: '#C7B381',
              marginBottom: '0.5rem',
              fontWeight: '500',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm password"
              style={{
                width: '100%',
                padding: 'clamp(0.9rem, 2vw, 1.1rem)',
                background: 'rgba(26, 31, 46, 0.6)',
                border: '1px solid rgba(199, 179, 129, 0.2)',
                borderRadius: '8px',
                fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
                color: '#FFFFFF',
                outline: 'none',
                transition: 'all 0.3s ease',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#C7B381';
                e.target.style.background = 'rgba(26, 31, 46, 0.8)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(199, 179, 129, 0.2)';
                e.target.style.background = 'rgba(26, 31, 46, 0.6)';
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: 'clamp(1rem, 2vw, 1.25rem)',
              background: loading ? 'rgba(199, 179, 129, 0.5)' : 'linear-gradient(135deg, #C7B381 0%, #A89968 100%)',
              color: '#0F1419',
              border: 'none',
              borderRadius: '8px',
              fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
              fontWeight: '600',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 16px rgba(199, 179, 129, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(199, 179, 129, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 16px rgba(199, 179, 129, 0.3)';
            }}
          >
            {loading ? 'Registering...' : 'Create Account'}
          </button>
        </form>

        {/* Login Link */}
        <div style={{
          marginTop: '2rem',
          textAlign: 'center',
          fontSize: 'clamp(0.85rem, 1.7vw, 0.9rem)',
          color: '#B8B8B8'
        }}>
          Already have an account?{' '}
          <Link 
            to="/login" 
            style={{
              color: '#C7B381',
              textDecoration: 'none',
              fontWeight: '500',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#E8D4A0'}
            onMouseLeave={(e) => e.target.style.color = '#C7B381'}
          >
            Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}

export default RegisterScreen;